import React, { useEffect, useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';

import LoadingSpinner from 'components/ui/loading_spinner';
import PlannerTable from 'bundles/apex/planner/table';
import Task from 'bundles/apex/tasks/task';
import { Button } from 'components/ui/button';
import { deleteRequest, get, put } from 'helpers/api';
import { getSortedTasks } from 'bundles/apex/planner/helpers';

export default function NewTasks({
  adminedOfficeIds,
  apexAdmin,
  attachmentsPath,
  canDeleteAttachments,
  fieldsPath,
  newDeal,
  oldDeal,
  requiredTasksEnabled,
  users,
  onHide,
}) {
  const [{ newTasks, existingTasks }] = useState(() => partitionTasks(oldDeal, newDeal));
  const [tasks, setTasks] = useState(newTasks);
  const [fields, setFields] = useState(null);

  useLayoutEffect(() => {
    if (!newDeal.accessible || tasks.length === 0) {
      close();
    }
  }, [tasks]);

  useEffect(() => {
    if (newTasks.length) {
      // New tasks are only relevant when a deal stage has changed, in which case its fields may
      // have changed too (e.g. date labels), so they're refetched and used here
      get(fieldsPath, { params: { dealId: newDeal.id, stage: newDeal.stage } }).then(setFields);
    }
  }, []);

  function close() {
    onHide({ ...newDeal, tasks: [...existingTasks, ...tasks] });
  }

  async function deleteTask(task) {
    const deletedTask = await deleteRequest(task.path);
    setTasks(tasks.filter(task => task.id !== deletedTask.id));
  }

  async function updateTask(task) {
    const updatedTask = await put(task.path, task);
    setTasks(tasks.map(task => task.id === updatedTask.id ? updatedTask : task));
  }

  function addTaskAttachment(task, attachment) {
    const updatedTask = { ...task };
    updatedTask.attachments.push(attachment);
    setTasks(tasks.map(task => task.id === updatedTask.id ? updatedTask : task));
  }

  function deleteTaskAttachment(task, attachment) {
    const updatedTask = {
      ...task,
      attachments: task.attachments.filter(taskAttachment => taskAttachment.id !== attachment.id)
    };
    setTasks(tasks.map(task => task.id === updatedTask.id ? updatedTask : task));
  }

  return (
    <>
      <span className="tasks-header">
        {oldDeal.id ? 'Deal updates' : 'New deal'} saved. The following deal tasks were created:
      </span>
      {!fields ? <LoadingSpinner /> :
        <PlannerTable className="my-3">
          {getSortedTasks({ tasks }).map(task => (
            <Task
              addTaskAttachment={addTaskAttachment}
              adminedOfficeIds={adminedOfficeIds}
              apexAdmin={apexAdmin}
              attachmentsPath={attachmentsPath}
              canDeleteAttachments={canDeleteAttachments}
              deal={newDeal}
              deleteTaskAttachment={deleteTaskAttachment}
              fields={fields}
              key={task.id}
              requiredTasksEnabled={requiredTasksEnabled}
              task={task}
              users={users}
              onDelete={deleteTask}
              onUpdate={updateTask}
            />
          ))}
        </PlannerTable>
      }
      <div className="text-end">
        <Button label="Close" variant="ghost" onClick={() => close()} />
      </div>
    </>
  );
}

function partitionTasks(oldDeal, newDeal) {
  const newTasks = newDeal.tasks.filter(task => !oldDeal.tasks.find(oldTask => oldTask.id === task.id));
  const newTaskIds = new Set(newTasks.map(task => task.id));
  return { newTasks, existingTasks: newDeal.tasks.filter(task => !newTaskIds.has(task.id)) };
}

NewTasks.propTypes = {
  adminedOfficeIds: PropTypes.array,
  apexAdmin: PropTypes.bool,
  attachmentsPath: PropTypes.string,
  canDeleteAttachments: PropTypes.bool.isRequired,
  fieldsPath: PropTypes.string,
  newDeal: PropTypes.object.isRequired,
  oldDeal: PropTypes.object.isRequired,
  requiredTasksEnabled: PropTypes.bool,
  users: PropTypes.array,
  onHide: PropTypes.func.isRequired,
};
